import { Grid, LazyBackgroundImage, TextField } from 'ui';
import { IconButton, Typography } from 'omakase';
import { LegacyPlaceholderBusinessImage, cropImageUrl } from '@tock/images';
import { PlaylistType } from 'utils/profileUtils';
import { consumer } from '@tock/coretypes';
import { getConcept, getLocality } from 'utils/businessUtils';
import { makeStyles } from '@material-ui/core/styles';
import Anchor from 'components/Anchor';
import PlaylistTypeIcon from './PlaylistTypeIcon';
import React, { useState } from 'react';

interface PlaylistBusinessProps {
  playlistItem: consumer.IPlaylistItem;
  isPublicPage?: boolean;
  playlistType?: PlaylistType;
  onRemovePlaylistItem?(playlistItemId: number): void;
  onChangeNote?(addRequest: consumer.IAddPlaylistItemRequest): void;
}

const usePlaylistBusinessStyles = makeStyles((theme) => ({
  imageWrapper: {
    flexShrink: 0,
    height: theme.spacing(9),
    position: 'relative',
    width: theme.spacing(9),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  imageWrapperDesktop: {
    flexShrink: 0,
    padding: theme.spacing(1),
    position: 'relative',
    height: '100%',
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  imageStyleMobile: {
    height: '100%',
    width: '100%',
    borderRadius: theme.borderRadius[100],
    minHeight: '1px',
    opacity: 0,
  },
  imageStyleDesktop: {
    borderRadius: theme.borderRadius[100],
    height: 'auto',
    minHeight: '1px',
    opacity: 0,
    paddingBottom: theme.mediaRatios.aspectRatio(204, 122),
    width: '100%',
  },
  imageStyleInView: {
    opacity: 1,
  },
  mainContent: {
    display: 'flex',
  },
  mainContentInner: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
    },
  },
  playlistBusinessWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    textDecoration: 'none',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1),
      paddingBottom: 0,
    },
  },
  playlistBusinessContainer: {
    backgroundColor: theme.colors.bgDefault,
    padding: theme.spacing(2),
  },
  playlistNote: {
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(2),
      marginRight: theme.spacing(1),
      ['.MuiGrid-spacing-xs-3 > &.MuiGrid-item']: {
        paddingTop: 0,
      },
    },
  },
  removeFromPlaylistButton: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.colors.borderDefault}`,
  },
  removeFromPlaylistButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

const PlaylistBusiness: React.FunctionComponent<PlaylistBusinessProps> = ({
  playlistItem,
  isPublicPage,
  playlistType,
  onRemovePlaylistItem,
  onChangeNote,
}) => {
  const [noteInternal, setNoteInternal] = useState(playlistItem.note || undefined);
  const business = playlistItem.business;
  const styles = usePlaylistBusinessStyles();

  if (!business) {
    return null;
  }

  const image = business.profileImage && business.profileImage[0];

  const { priceRange } = business;
  const locality = getLocality(business);
  const subtitle = [locality, getConcept(business), priceRange].filter(Boolean).join(' · ');

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNote = e.target.value;

    if (newNote.length > 255) {
      return;
    }

    setNoteInternal(newNote);
    onChangeNote &&
      onChangeNote({ businessId: playlistItem.business?.id, id: playlistItem.id, note: newNote });
  };

  const handleRemoveItem = () => {
    window.analytics.sendEvent('Playlist', 'Business removed', business.name);
    playlistItem.id && onRemovePlaylistItem && onRemovePlaylistItem(playlistItem.id);
  };

  return (
    <Grid container className={styles.playlistBusinessContainer}>
      <Grid item md={3}>
        <div className={styles.imageWrapperDesktop}>
          <LazyBackgroundImage
            aria-label={image?.altText ?? undefined}
            backgroundColor={image?.dominantColor ?? undefined}
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            backgroundSize="cover"
            className={styles.imageStyleDesktop}
            role="img"
            url={cropImageUrl(image?.imageUrl || LegacyPlaceholderBusinessImage, 220, 133)}
            inViewClassName={styles.imageStyleInView}
          />
        </div>
      </Grid>
      <Grid className={styles.mainContent} item md={9} xs={12}>
        <Grid container spacing={3} justify="space-between" className={styles.mainContentInner}>
          <Grid item xs={12} md={10}>
            <Anchor
              href={`/${business.domainName}`}
              onClick={() =>
                window.analytics.sendEvent('Playlist', 'Business clicked', business.name, {
                  transport: 'beacon',
                })
              }
              className={styles.playlistBusinessWrapper}
            >
              <div>
                <Typography variant="body01Strong">{business.name}</Typography>
                <Typography variant="body02" mb={2}>
                  {subtitle}
                </Typography>
                {isPublicPage && playlistItem.note ? (
                  <Typography variant="body02">{playlistItem.note}</Typography>
                ) : null}
              </div>
              <div className={styles.imageWrapper}>
                <LazyBackgroundImage
                  aria-label={image?.altText ?? undefined}
                  backgroundColor={image?.dominantColor ?? undefined}
                  backgroundPosition="center"
                  backgroundRepeat="no-repeat"
                  backgroundSize="cover"
                  className={styles.imageStyleMobile}
                  role="img"
                  url={cropImageUrl(image?.imageUrl || LegacyPlaceholderBusinessImage, 175, 175)}
                  inViewClassName={styles.imageStyleInView}
                />
              </div>
            </Anchor>
          </Grid>
          {!isPublicPage && onRemovePlaylistItem && (
            <Grid item xs={2} className={styles.removeFromPlaylistButtonContainer}>
              <IconButton
                onClick={handleRemoveItem}
                variant="basic"
                size="medium"
                className={styles.removeFromPlaylistButton}
                aria-label="Remove business from list"
              >
                <PlaylistTypeIcon playlistType={playlistType} filled />
              </IconButton>
            </Grid>
          )}
          {!isPublicPage ? (
            <Grid item xs={12} className={styles.playlistNote}>
              <PlaylistNoteField value={noteInternal || ''} onChange={handleNoteChange} />
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

interface PlaylistNoteFieldProps {
  value: string;
  onChange(e: React.ChangeEvent<HTMLInputElement>): void;
}

const PlaylistNoteField: React.FunctionComponent<PlaylistNoteFieldProps> = ({
  value,
  onChange,
}) => {
  return (
    <TextField
      fullWidth
      multiline
      value={value}
      onChange={onChange}
      placeholder="Add a note about this place?"
      label="Note"
    />
  );
};

export default PlaylistBusiness;
